import { getWhatsAppStatus, Sector } from '../../services/SectorService';
import { getEmailConnectionStatus } from '../../services/EmailConnectionService';
import { getCalendarConnectionStatus } from '../../services/CalendarService';
import { listGoogleAdsLeadIntegrations } from '../../services/GoogleAdsLeadService';
import {
  getMetaConnectionStatus,
  metaAccountStateLabel,
  NOT_CONFIGURED_MESSAGE,
  type MetaAccountStatus,
  type MetaAccountChannel,
  type MetaChannel,
  type MetaConnectionStatus as MetaStatus,
} from '../../services/MetaConnectionService';

/**
 * online = conectado agora · offline = configurado mas caiu (afeta a operação) ·
 * not_configured = nunca conectado neste setor (não é falha) · unavailable/unknown = não deu para consultar.
 */
export type ChannelStatus = 'online' | 'offline' | 'not_configured' | 'unknown' | 'unavailable';

export interface ConnectedChannel {
  id: string;
  label: string;
  detail?: string;
  status: ChannelStatus;
  /** Fonte real da API — para auditoria / relatório */
  source: string;
  href?: string;
}

function mapOnline(ok: boolean): ChannelStatus {
  return ok ? 'online' : 'offline';
}

/** Do mais grave ao menos grave: o pior estado das contas de um canal manda no chip. */
const META_STATE_SEVERITY: Record<string, number> = {
  needs_reconnect: 4,
  webhook_unsubscribed: 3,
  policy_warning: 2,
};

function metaAccountName(a: MetaAccountStatus): string {
  if (a.channel === 'whatsapp') return `WhatsApp oficial${a.displayPhone ? ` · ${a.displayPhone}` : a.name ? ` · ${a.name}` : ''}`;
  return a.channel === 'instagram' && a.username ? `@${a.username}` : a.name || a.accountId;
}

/** A tela de problemas já fecha a frase com ponto: o detalhe não pode trazer o seu. */
function withoutFinalPeriod(text: string): string {
  const t = text.trimEnd();
  return t.endsWith('.') ? t.slice(0, -1) : t;
}

/** Chip de Messenger ou Instagram a partir das contas da Meta do setor. Nunca "online" sem conta ativa. */
function metaChannel(
  id: MetaAccountChannel,
  label: string,
  accounts: MetaAccountStatus[],
  configured: boolean,
): ConnectedChannel {
  const base = { id, label, source: 'MetaConnection/status', href: '/sectors' };
  if (!configured) return { ...base, status: 'not_configured', detail: NOT_CONFIGURED_MESSAGE };
  const mine = accounts.filter((a) => a.channel === id);
  if (mine.length === 0) return { ...base, status: 'not_configured', detail: 'Não conectado' };

  const worst = mine
    .filter((a) => a.status !== 'active')
    .sort((a, b) => (META_STATE_SEVERITY[b.status] ?? 1) - (META_STATE_SEVERITY[a.status] ?? 1))[0];
  if (worst) {
    const reason = metaAccountStateLabel(worst.status);
    return {
      ...base,
      status: 'offline',
      detail:
        worst.status === 'policy_warning' && worst.lastError
          ? withoutFinalPeriod(worst.lastError)
          : `${reason}: ${metaAccountName(worst)}`,
    };
  }
  const failed = mine.reduce((sum, a) => sum + (a.failedEvents || 0), 0);
  if (failed > 0) {
    return {
      ...base,
      status: 'offline',
      detail: `${failed} ${failed === 1 ? 'mensagem recebida não foi processada' : 'mensagens recebidas não foram processadas'}`,
    };
  }
  return { ...base, status: 'online', detail: mine.map(metaAccountName).join(', ') };
}

/**
 * Canais que o CRM realmente conhece para o setor.
 * Não inventa SMS — só o que há integração. Messenger e Instagram vêm das contas da Meta conectadas ao setor.
 */
export async function fetchConnectedChannels(
  sectorId: number,
  sector?: Sector | null,
): Promise<ConnectedChannel[]> {
  const channels: ConnectedChannel[] = [];

  // Status da Meta uma vez só: vale para o WhatsApp oficial (Cloud API), o Messenger e o Instagram.
  let meta: MetaStatus | null = null;
  try {
    meta = await getMetaConnectionStatus(sectorId);
  } catch {
    meta = null;
  }

  // WhatsApp (oficial direto na Meta / linha anterior / QR)
  const cloudAccounts = (meta?.accounts || []).filter((a) => a.channel === 'whatsapp');
  try {
    const isSpark = sector?.isOfficial === true;
    if (cloudAccounts.length > 0) {
      // Setor com conta da Cloud API: o chip mostra o status real dela (a linha anterior já foi desligada).
      channels.push({ ...metaChannel('whatsapp', 'WhatsApp', cloudAccounts, true), href: '/sectors' });
    } else if (isSpark) {
      const online = sector?.sparkIsActive === true;
      channels.push({
        id: 'whatsapp-spark',
        label: 'WhatsApp',
        detail: sector?.sparkLinePhone ? `WhatsApp oficial · ${sector.sparkLinePhone}` : 'WhatsApp oficial',
        status: mapOnline(online),
        source: 'sector.sparkIsActive',
        href: '/sectors',
      });
    } else {
      const wa = await getWhatsAppStatus(sectorId, false);
      channels.push({
        id: 'whatsapp',
        label: 'WhatsApp',
        detail: wa.message || wa.status,
        status: mapOnline(wa.status === 'connected' || wa.connected === true),
        source: 'getWhatsAppStatus',
        href: '/sectors',
      });
    }
  } catch {
    channels.push({
      id: 'whatsapp',
      label: 'WhatsApp',
      detail: 'Falha ao consultar status',
      status: 'unknown',
      source: 'getWhatsAppStatus',
      href: '/sectors',
    });
  }

  // E-mail
  try {
    const email = await getEmailConnectionStatus(sectorId);
    const online = email.hasConnection && String(email.status || '').toLowerCase() !== 'disconnected';
    channels.push({
      id: 'email',
      label: 'E-mail',
      detail: email.provider || (email.hasConnection ? 'Conectado' : 'Não conectado'),
      status: email.hasConnection ? mapOnline(online) : 'not_configured',
      source: 'EmailConnection/status',
      href: '/sectors',
    });
  } catch {
    channels.push({
      id: 'email',
      label: 'E-mail',
      detail: 'Indisponível nesta conta',
      status: 'unavailable',
      source: 'EmailConnection/status',
      href: '/sectors',
    });
  }

  // Messenger e Instagram (Meta)
  if (meta) {
    const accounts = meta.accounts || [];
    // Cada canal tem a sua configuração (Instagram e Messenger usam logins e chaves diferentes).
    const configured = (c: MetaChannel) =>
      (c === 'instagram' ? meta!.instagram : meta!.messenger)?.configured ?? meta!.configured !== false;
    channels.push(metaChannel('messenger', 'Messenger', accounts, configured('messenger')));
    channels.push(metaChannel('instagram', 'Instagram', accounts, configured('instagram')));
  } else {
    for (const [id, label] of [['messenger', 'Messenger'], ['instagram', 'Instagram']] as const) {
      channels.push({
        id,
        label,
        detail: 'Indisponível',
        status: 'unavailable',
        source: 'MetaConnection/status',
        href: '/sectors',
      });
    }
  }

  // Google Calendar (agenda)
  try {
    const cal = await getCalendarConnectionStatus(sectorId);
    // Token recusado pelo Google: a agenda parou de sincronizar, então é falha (não "nunca conectado").
    const needsReconnect = cal.needsReconnect === true || cal.status === 'needs_reconnect';
    channels.push({
      id: 'calendar',
      label: 'Google Calendar',
      detail: needsReconnect
        ? `${cal.googleEmail ? `${cal.googleEmail}: o` : 'O'} Google recusou o acesso. Conecte a agenda de novo`
        : cal.googleEmail || cal.calendarSummary || (cal.hasConnection ? 'Conectado' : 'Não conectado'),
      status: needsReconnect
        ? 'offline'
        : cal.hasConnection ? mapOnline(String(cal.status || '').toLowerCase() !== 'disconnected') : 'not_configured',
      source: 'CalendarConnection/status',
      href: needsReconnect ? '/sectors' : '/agenda',
    });
  } catch {
    channels.push({
      id: 'calendar',
      label: 'Google Calendar',
      detail: 'Indisponível',
      status: 'unavailable',
      source: 'CalendarConnection/status',
      href: '/agenda',
    });
  }

  // Google Ads Leads (formulários)
  try {
    const ads = await listGoogleAdsLeadIntegrations(sectorId);
    const active = (ads || []).filter((i) => i.enabled !== false);
    channels.push({
      id: 'google-ads',
      label: 'Google Ads',
      detail: active.length
        ? `${active.length} integração(ões)`
        : (ads?.length ? 'Integrações inativas' : 'Sem integração'),
      // Sem integração = não configurado; integrações existentes porém desativadas = offline.
      status: active.length > 0 ? 'online' : (ads?.length ? 'offline' : 'not_configured'),
      source: 'GoogleAdsLeadIntegrations',
      href: '/google-ads-leads',
    });
  } catch {
    channels.push({
      id: 'google-ads',
      label: 'Google Ads',
      detail: 'Não configurado / sem permissão',
      status: 'unavailable',
      source: 'GoogleAdsLeadIntegrations',
      href: '/google-ads-leads',
    });
  }

  return channels;
}
